/**
 * In-app browser detection + "open in real browser" helpers.
 *
 * ⚠ Reality check (2026):
 *   • `intent://` on Android: Facebook / Instagram / TikTok actively intercept
 *     and kill these URLs inside their in-app WebView to prevent exits. The
 *     scheme writes succeed from JS but the OS never acts on them.
 *   • `x-safari-https://` on iOS: Apple deprecated this undocumented scheme
 *     around iOS 14.5 (2021). It no longer registers.
 *   • The only *reliable* escape from a modern in-app WebView is a real user
 *     tap on a real `<a target="_blank" rel="noopener noreferrer">` anchor —
 *     that triggers the WebView's own "open externally" delegate, which the
 *     host app usually honours.
 *
 * This module therefore provides:
 *   • `detectInAppBrowser()` — unchanged, UA-based detection.
 *   • `openUrlInSystemBrowser(url)` — JS-driven attempt (best effort) that
 *     ALWAYS falls back to normal in-WebView navigation after 800 ms so the
 *     shopper is never left on an infinite "Redirecting…" button.
 *   • `buildIntentUrl(url)` / `buildSafariUrl(url)` — exposed so the banner
 *     can render them as real `<a href>` anchors (the reliable path).
 */

const UA_IN_APP_PATTERNS = [
  "FBAN", "FBAV", "FB_IAB",
  "Instagram",
  "TikTok", "BytedanceWebview",
  "Twitter",
  "LinkedInApp",
  "Snapchat",
  "Pinterest",
  "Line/",
  "KAKAOTALK",
];

export function detectInAppBrowser() {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  if (!isIOS && !isAndroid) return null;

  const hit = UA_IN_APP_PATTERNS.find((p) => ua.includes(p));
  if (hit) return { source: hit, platform: isIOS ? "ios" : "android" };

  if (isIOS && /AppleWebKit/.test(ua) && !/Safari\//.test(ua)) {
    return { source: "WKWebView", platform: "ios" };
  }
  if (isAndroid && /; wv\)/.test(ua)) {
    return { source: "AndroidWebView", platform: "android" };
  }
  return null;
}

/** Build the Android Chrome intent URL for `targetUrl`. */
export function buildIntentUrl(targetUrl) {
  const stripped = String(targetUrl).replace(/^https?:\/\//, "");
  const fallback = encodeURIComponent(String(targetUrl));
  return (
    `intent://${stripped}` +
    `#Intent;scheme=https;package=com.android.chrome;` +
    `S.browser_fallback_url=${fallback};end`
  );
}

/**
 * JS-driven hand-off. Returns true if an attempt was made.
 *
 * ⚠ The hand-off is BEST EFFORT. Modern FB / IG / TikTok WebViews often
 * silently block these URL schemes. We always schedule a fallback that
 * navigates the current WebView to `targetUrl` after 800 ms so a stuck
 * shopper lands on the Shopify checkout page in-WebView instead of staring
 * at an infinite "Redirecting…" button. Card-entry on Shopify still works
 * in a WebView even when Shop Pay / Apple Pay don't.
 */
export function openUrlInSystemBrowser(targetUrl) {
  const hit = detectInAppBrowser();
  if (!hit || typeof window === "undefined") return false;

  // Schedule the safety fallback FIRST so a thrown exception on the scheme
  // write still can't strand the shopper. Cancelled naturally by the
  // page unloading if the OS actually honoured the scheme.
  const fallbackTimer = window.setTimeout(() => {
    try { window.location.href = targetUrl; } catch { /* noop */ }
  }, 800);

  // Best-effort scheme write. If this throws (invalid URL on this UA), the
  // 800 ms timer still carries the shopper through.
  try {
    if (hit.platform === "ios") {
      window.location.href = String(targetUrl).replace(/^https?:/, "x-safari-https:");
    } else if (hit.platform === "android") {
      window.location.href = buildIntentUrl(targetUrl);
    }
  } catch { /* noop */ }

  // If the OS does honour the scheme, visibilitychange fires — cancel the
  // in-WebView fallback so we don't trigger a double navigation.
  const onHide = () => {
    if (document.hidden) {
      window.clearTimeout(fallbackTimer);
      document.removeEventListener("visibilitychange", onHide);
    }
  };
  document.addEventListener("visibilitychange", onHide);

  return true;
}
